// RefundPolicy.jsx — Redesigned: white bg, navy headings, black text, AOS animations
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import "./Refund.css";
import {
  PRODUCT_TABS as FALLBACK_PRODUCT_TABS,
  POLICY_STAGES_BY_TAB as FALLBACK_POLICY_STAGES_BY_TAB,
  TESTIMONIALS as FALLBACK_TESTIMONIALS,
  INITIAL_VISIBLE,
  LOAD_MORE_STEP,
  DEFAULT_ACTIVE_TAB,
  STAR_VALUES,
  STAR_TOTAL,
  TABLET_BREAKPOINT_MIN,
  TABLET_BREAKPOINT_MAX,
  CARDS_PER_PAGE_TABLET,
  CARDS_PER_PAGE_MOBILE,
  GRID_COLUMNS,
  AOS_DELAY_MULTIPLIER,
  REVIEW_MIN_LENGTH,
  REVIEW_MAX_LENGTH,
  NAME_MAX_LENGTH,
  TOAST_DURATION,
  DEFAULT_LOCATION,
  DEFAULT_TAG,
  DATE_LOCALE,
  DATE_OPTIONS,
  HERO_TITLE,
  HERO_SUBTITLE,
  TABLE_HEADERS,
  SHOW_FEWER_LABEL,
  LOAD_MORE_LABEL,
  SUBMIT_REVIEW_LABEL,
  EMPTY_REVIEWS_MESSAGE,
  WRITE_REVIEW_TITLE,
  WRITE_REVIEW_DESC,
  FORM_LABEL_NAME,
  FORM_LABEL_LOCATION,
  FORM_LABEL_CATEGORY,
  FORM_LABEL_RATING,
  FORM_LABEL_REVIEW,
  FORM_PLACEHOLDER_NAME,
  FORM_PLACEHOLDER_LOCATION,
  FORM_PLACEHOLDER_REVIEW,
  VISA_CATEGORIES,
  EMPTY_FORM,
  REVIEWS_SECTION_TITLE,
  ERROR_NAME_REQUIRED,
  ERROR_RATING_REQUIRED,
  ERROR_REVIEW_MIN_LENGTH,
  TOAST_SUCCESS,
} from "./Policy.js";
import configService from "../../../../services/configService";
import { API_BASE_URL } from "../../../../config";

/* ─── Star Rating ─────────────────────────────────────── */
function StarRating({ count, total = STAR_TOTAL }) {
  const safe = typeof count === "number" ? count : parseInt(count, 10) || 0;
  return (
    <div className="R-stars">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className="R-star" style={{ color: i < safe ? "rgb(245, 158, 11)" : "var(--R-gray-4)" }}>
          &#9733;
        </span>
      ))}
    </div>
  );
}

/* ─── Badge ───────────────────────────────────────────── */
function Badge({ type, label }) {
  const displayLabel = /^\d+%$/.test(label) ? `${label} Refund` : label;
  return <span className={`R-badge R-badge--${type}`}>{displayLabel}</span>;
}

/* ─── Hero & Policy Section (merged) ───────────────────── */
function HeroPolicySection({ productTabs, policyStages }) {
  const [activeTab, setActiveTab] = useState(() => productTabs[0]?.id || DEFAULT_ACTIVE_TAB);
  const stages = policyStages[activeTab] || [];

  useEffect(() => {
    if (productTabs.length > 0 && !productTabs.some(t => t.id === activeTab)) {
      setActiveTab(productTabs[0].id);
    }
  }, [productTabs]);

  return (
    <section className="R-hero-policy-section" data-aos="fade-down">
      <div className="R-container">
        <div className="R-hero__inner">
          <h1 className="R-hero__title" data-aos="fade-up" data-aos-delay="200">
            {HERO_TITLE}
          </h1>
          <p className="R-hero__sub" data-aos="fade-up" data-aos-delay="300">
            {HERO_SUBTITLE}
          </p>
          <div className="R-hero__rule" data-aos="scale-x" data-aos-delay="400" />
        </div>

        <div className="R-policy-block">
          <div className="R-tabs" data-aos="fade-left" data-aos-delay="100">
            {productTabs.map((tab) => (
              <button
                key={tab.id}
                className={`R-tab${activeTab === tab.id ? " R-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                title={tab.description}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="R-table-wrap" data-aos="fade-up" data-aos-delay="150">
            <table className="R-table">
              <thead>
                <tr>
                  {TABLE_HEADERS.map((header, i) => (
                  <th key={i} className="R-table__th">{header}</th>
                ))}
                </tr>
              </thead>
              <tbody>
                {stages.map((row, i) => (
                  <tr
                    key={row.id}
                    className="R-table__row"
                    data-aos="fade-up"
                    data-aos-delay={i * 50}
                  >
                    <td className="R-table__td">
                      <span className="R-table__stage">{row.stage}</span>
                      {row.substage && (
                        <span className="R-table__substage">{row.substage}</span>
                      )}
                    </td>
                    <td className="R-table__td">
                      <Badge type={row.refundType} label={row.refundStatus} />
                    </td>
                    <td className="R-table__td R-table__td--reason">{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Review Card ─────────────────────────────────────── */
function ReviewCard({ review, revealed = false, aosDelay = 0 }) {
  return (
    <div
      className={`R-review-card${revealed ? " R-review-card--revealed" : ""}`}
      data-aos={aosDelay % (AOS_DELAY_MULTIPLIER * 2) === 0 ? "fade-right" : "fade-left"}
      data-aos-delay={aosDelay}
    >
      <div className="R-review-card__top">
        <div className="R-avatar">{review.initials}</div>
        <div className="R-review-card__meta">
          <span className="R-review-card__name">{review.name}</span>
          <span className="R-review-card__info">
            {review.location} &middot; {review.date}
          </span>
        </div>
        <span className="R-review-card__tag">{review.tag}</span>
      </div>
      <StarRating count={review.rating} />
      <p className="R-review-card__text">{review.review}</p>
    </div>
  );
}

/* ─── View More Button ────────────────────────────────── */
function ViewMoreButton({ visible, total, onViewMore, onShowLess }) {
  const remaining = total - visible;
  const isExpanded = visible >= total;
  if (total <= INITIAL_VISIBLE) return null;

  return (
    <div className="R-viewmore-wrap" data-aos="fade-up">
      {isExpanded ? (
        <button className="R-btn R-btn--outline" onClick={onShowLess}>
          {SHOW_FEWER_LABEL}
        </button>
      ) : (
        <button className="R-btn R-btn--primary" onClick={onViewMore}>
          {LOAD_MORE_LABEL}
        </button>
      )}
    </div>
  );
}

/* ─── Desktop Grid ────────────────────────────────────── */
function DesktopGrid({ reviews }) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [revealedFrom, setRevealedFrom] = useState(null);
  const gridRef = useRef(null);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
    setRevealedFrom(null);
  }, [reviews]);

  function handleViewMore() {
    const prev = visibleCount;
    const next = Math.min(visibleCount + LOAD_MORE_STEP, reviews.length);
    setRevealedFrom(prev);
    setVisibleCount(next);
  }

  function handleShowLess() {
    setVisibleCount(INITIAL_VISIBLE);
    setRevealedFrom(null);
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (reviews.length === 0) {
    return (
      <div className="R-empty" data-aos="fade-up">
        {EMPTY_REVIEWS_MESSAGE}
      </div>
    );
  }

  const visible = reviews.slice(0, visibleCount);

  const isCollapsed = reviews.length > INITIAL_VISIBLE;

  return (
    <>
      <div className={`R-reviews-grid${isCollapsed ? " R-reviews-grid--collapsed" : ""}`} ref={gridRef}>
        {visible.map((r, i) => (
          <ReviewCard
            key={r.id}
            review={r}
            revealed={revealedFrom !== null && i >= revealedFrom}
            aosDelay={(i % GRID_COLUMNS) * AOS_DELAY_MULTIPLIER}
          />
        ))}
      </div>
      <ViewMoreButton
        visible={visibleCount}
        total={reviews.length}
        onViewMore={handleViewMore}
        onShowLess={handleShowLess}
      />
    </>
  );
}

/* ─── Carousel (tablet/mobile) ────────────────────────── */
function ReviewCarousel({ reviews }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [isTablet, setIsTablet] = useState(false);
  const autoRef = useRef(null);

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      setIsTablet(w >= TABLET_BREAKPOINT_MIN && w <= TABLET_BREAKPOINT_MAX);
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => { setPageIndex(0); }, [reviews.length]);

  const perPage = isTablet ? CARDS_PER_PAGE_TABLET : CARDS_PER_PAGE_MOBILE;
  const pages = [];
  for (let i = 0; i < reviews.length; i += perPage) {
    pages.push(reviews.slice(i, i + perPage));
  }
  const maxPage = Math.max(0, pages.length - 1);
  const prev = useCallback(() => setPageIndex(p => Math.max(0, p - 1)), []);
  const next = useCallback(() => setPageIndex(p => Math.min(maxPage, p + 1)), [maxPage]);

  useEffect(() => {
    autoRef.current = setInterval(() => {
      setPageIndex(prev => {
        const nextPage = prev + 1;
        return nextPage > maxPage ? 0 : nextPage;
      });
    }, 3000);
    return () => clearInterval(autoRef.current);
  }, [maxPage]);

  function handlePrev() {
    clearInterval(autoRef.current);
    prev();
  }

  function handleNext() {
    clearInterval(autoRef.current);
    next();
  }

  function handleDotClick(i) {
    clearInterval(autoRef.current);
    setPageIndex(i);
  }

  if (reviews.length === 0) {
    return (
      <div className="R-carousel" data-aos="fade-up">
        <div className="R-empty">{EMPTY_REVIEWS_MESSAGE}</div>
      </div>
    );
  }

  return (
    <div className="R-carousel" data-aos="fade-up">
      <div className="R-carousel__wrap">
        <div
          className="R-carousel__track"
          style={{ "--offset": `${-pageIndex * 100}%` }}
        >
          {pages.map((pageCards, pi) => (
            <div key={pi} className="R-carousel__slide">
              {isTablet ? (
                <div className="R-carousel__page-grid">
                  {pageCards.map(r => <ReviewCard key={r.id} review={r} />)}
                </div>
              ) : (
                pageCards.map(r => <ReviewCard key={r.id} review={r} />)
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="R-carousel__controls">
        <button
          className="R-carousel__btn"
          onClick={handlePrev}
          disabled={pageIndex === 0}
          aria-label="Previous"
        >
          &#8592;
        </button>
        <div className="R-carousel__dots">
          {pages.map((_, i) => (
            <button
              key={i}
              className={`R-carousel__dot${pageIndex === i ? " R-carousel__dot--active" : ""}`}
              onClick={() => handleDotClick(i)}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </div>
        <button
          className="R-carousel__btn"
          onClick={handleNext}
          disabled={pageIndex >= maxPage}
          aria-label="Next"
        >
          &#8594;
        </button>
      </div>
    </div>
  );
}

/* ─── Star Picker ─────────────────────────────────────── */
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="R-star-picker">
      {STAR_VALUES.map(n => (
        <button
          key={n}
          type="button"
          className={`R-star-btn${n <= (hover || value) ? " R-star-btn--lit" : ""}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
        >
          &#9733;
        </button>
      ))}
    </div>
  );
}

/* ─── Write Review ────────────────────────────────────── */
function WriteReview({ productTabs }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const categoryOptions = useMemo(() => {
    if (productTabs && productTabs.length > 0) {
      return [
        { value: "", label: "Select category" },
        ...productTabs.map(tab => ({ value: tab.label, label: tab.label })),
      ];
    }
    return VISA_CATEGORIES;
  }, [productTabs]);

  function handleChange(e) {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setError("");
  }

  async function handleSubmit() {
    if (!form.name.trim()) return setError(ERROR_NAME_REQUIRED);
    if (form.rating === 0) return setError(ERROR_RATING_REQUIRED);
    if (form.review.trim().length < REVIEW_MIN_LENGTH) return setError(ERROR_REVIEW_MIN_LENGTH);

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tvc/refund/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          location: form.location.trim(),
          rating: form.rating,
          tag: form.tag || DEFAULT_TAG,
          review: form.review.trim(),
        }),
      });
      if (!res.ok) {
        let msg = "Failed to submit review";
        try { const d = await res.json(); msg = d.error || msg; } catch {}
        throw new Error(msg);
      }
      setForm(EMPTY_FORM);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), TOAST_DURATION);
    } catch (err) {
      if (err.name === "TypeError" && err.message === "Failed to fetch") {
        setError("Unable to reach the server. Please check your connection or try again later.");
      } else {
        setError(err.message || "Failed to submit review");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="R-write-review" data-aos="fade-right">
      <div className="R-write-review__header">
        <h3 className="R-write-review__title">{WRITE_REVIEW_TITLE}</h3>
        <p className="R-write-review__desc">
          {WRITE_REVIEW_DESC}
        </p>
      </div>

      <div className="R-write-review__form">
        <div className="R-field">
          <label className="R-label">{FORM_LABEL_NAME} <span className="R-req">*</span></label>
          <input className="R-input" name="name" value={form.name} onChange={handleChange} placeholder={FORM_PLACEHOLDER_NAME} maxLength={NAME_MAX_LENGTH} />
        </div>
        <div className="R-field">
          <label className="R-label">{FORM_LABEL_LOCATION}</label>
          <input className="R-input" name="location" value={form.location} onChange={handleChange} placeholder={FORM_PLACEHOLDER_LOCATION} maxLength={NAME_MAX_LENGTH} />
        </div>
        <div className="R-field">
          <label className="R-label">{FORM_LABEL_CATEGORY}</label>
          <select className="R-select" name="tag" value={form.tag} onChange={handleChange}>
            {categoryOptions.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div className="R-field">
          <label className="R-label">{FORM_LABEL_RATING} <span className="R-req">*</span></label>
          <StarPicker value={form.rating} onChange={v => { setForm(p => ({ ...p, rating: v })); setError(""); }} />
        </div>
        <div className="R-field R-field--full">
          <label className="R-label">{FORM_LABEL_REVIEW} <span className="R-req">*</span></label>
          <div className="R-textarea-wrap">
            <textarea
              className="R-textarea"
              name="review"
              value={form.review}
              onChange={handleChange}
              placeholder={FORM_PLACEHOLDER_REVIEW}
              maxLength={REVIEW_MAX_LENGTH}
            />
            {!error && (
              <span className="R-char-count">{form.review.length} / {REVIEW_MAX_LENGTH}</span>
            )}
          </div>
        </div>
        <div className="R-form-footer">
          {error && <span className="R-error">{error}</span>}
          <button
            className="R-btn R-btn--primary"
            onClick={handleSubmit}
            disabled={!form.name || !form.review || loading}
          >
            {loading ? "Submitting..." : SUBMIT_REVIEW_LABEL}
          </button>
        </div>
      </div>

      {submitted && (
        <div className="R-toast">
          {TOAST_SUCCESS}
        </div>
      )}
    </div>
  );
}

/* ─── Reviews Section ─────────────────────────────────── */
function ReviewsSection({ testimonials, productTabs }) {
  const [reviews, setReviews] = useState(testimonials);
  const [ratingFilter, setRatingFilter] = useState(0);

  useEffect(() => {
    setReviews(testimonials);
  }, [testimonials]);

  const filtered = useMemo(() => {
    if (ratingFilter === 0) return reviews;
    return reviews.filter(r => r.rating === ratingFilter);
  }, [ratingFilter, reviews]);

  const avgRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((a, r) => a + Number(r.rating), 0);
    return (sum / reviews.length).toFixed(1);
  }, [reviews]);

  const filterOptions = [
    { label: "All", value: 0 },
    { label: "5", value: 5 },
    { label: "4", value: 4 },
    { label: "3", value: 3 },
    { label: "2", value: 2 },
    { label: "1", value: 1 },
  ];

  return (
    <section className="R-reviews-section">
      <div className="R-container">
        <div className="R-section-head aos-init aos-animate" data-aos="fade-left">
          <h2 className="R-section-title">{REVIEWS_SECTION_TITLE}</h2>
          <p className="R-section-sub">See what our customers say about their refund experience</p>
        </div>

        <div className="R-reviews-toolbar">
          {/* <div className="R-aggregate" data-aos="fade-up">
            <div className="R-aggregate__stars">
              {[1,2,3,4,5].map(s => (
                <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              ))}
            </div>
            <span className="R-aggregate__score">{avgRating}</span>
            <span className="R-aggregate__count">from {reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
          </div> */}
          {/* <div className="R-filter-bar aos-init aos-animate" data-aos="fade-up">
            <select
              className="R-select"
              value={ratingFilter}
              onChange={(e) => setRatingFilter(Number(e.target.value))}
              style={{ minWidth: 60 }}
            >
              {filterOptions.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div> */}
        </div>

        <div className="R-reviews-two-col">
          <div className="R-reviews-left">
            <WriteReview productTabs={productTabs} />
          </div>
          <div className="R-reviews-right">
            <div className="R-desktop-grid">
              <DesktopGrid reviews={filtered} />
            </div>
            <ReviewCarousel reviews={filtered} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Root ────────────────────────────────────────────── */
export default function RefundPolicy() {
  const [productTabs, setProductTabs] = useState(FALLBACK_PRODUCT_TABS);
  const [policyStages, setPolicyStages] = useState(FALLBACK_POLICY_STAGES_BY_TAB);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);

  useEffect(() => {
    configService.getConfig().then(r => {
      if (r.data?.refundPolicy) {
        if (r.data.refundPolicy.productTabs) setProductTabs(r.data.refundPolicy.productTabs);
        if (r.data.refundPolicy.policyStages) setPolicyStages(r.data.refundPolicy.policyStages);
        if (r.data.refundPolicy.testimonials) setTestimonials(r.data.refundPolicy.testimonials);
      }
    }).catch((err) => console.warn("Failed to load config:", err));
  }, []);

  return (
    <div className="R-page">
      <HeroPolicySection productTabs={productTabs} policyStages={policyStages} />
      <ReviewsSection testimonials={testimonials} productTabs={productTabs} />
    </div>
  );
}
